<template>
  <div>
    <AppMenu />
    <BaseBreadcrumb title="Tiempos de cocina" :breadcrumbs="breadcrumbs"></BaseBreadcrumb>

    <v-card class="mt-3 mx-3 pa-4">
      <div class="d-flex flex-wrap align-center filtros">
        <v-btn-toggle v-model="periodo" mandatory rounded dense color="#a31d48" @change="elegirPeriodo">
          <v-btn value="hoy" small>Hoy</v-btn>
          <v-btn value="7" small>7 días</v-btn>
          <v-btn value="30" small>30 días</v-btn>
          <v-btn value="otro" small>Otro</v-btn>
        </v-btn-toggle>
        <template v-if="periodo === 'otro'">
          <v-text-field v-model="desde" type="date" label="Desde" dense hide-details outlined class="filtros__fecha" />
          <v-text-field v-model="hasta" type="date" label="Hasta" dense hide-details outlined class="filtros__fecha" />
          <v-btn small depressed dark color="#a31d48" @click="cargar">Ver</v-btn>
        </template>
        <v-spacer></v-spacer>
        <span class="grey--text text--darken-1">Pedidos terminados en la sucursal {{ sucursalNombre }}</span>
      </div>

      <v-progress-linear v-if="cargando" indeterminate color="#a31d48" class="mt-3" />

      <div v-if="datos && datos.resumen.pedidos === 0 && !cargando" class="vacio">
        <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
        <p>No hay pedidos terminados por la cocina en estas fechas.</p>
      </div>

      <template v-if="datos && datos.resumen.pedidos > 0">
        <v-row class="mt-3">
          <v-col cols="12" sm="6" md="3">
            <div class="dato"><span class="dato__titulo">Pedidos</span><span class="dato__valor">{{ datos.resumen.pedidos }}</span></div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="dato">
              <span class="dato__titulo">Espera para aceptar</span>
              <span class="dato__valor">{{ minutos(datos.resumen.espera_promedio) }}</span>
              <span class="dato__nota">Desde que entra hasta «Preparar»</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="dato">
              <span class="dato__titulo">Preparación promedio</span>
              <span class="dato__valor">{{ minutos(datos.resumen.preparacion_promedio) }}</span>
              <span class="dato__nota">Mediana {{ minutos(datos.resumen.preparacion_mediana) }}</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="dato">
              <span class="dato__titulo">La más lenta</span>
              <span class="dato__valor">{{ minutos(datos.resumen.preparacion_maxima) }}</span>
              <span class="dato__nota">Preparación de un solo pedido</span>
            </div>
          </v-col>
        </v-row>

        <h3 class="seccion">Horas pico</h3>
        <p class="grey--text text--darken-1 mb-2">
          Cada barra son los pedidos que llegaron en esa hora; el número de arriba es lo que tardó la cocina en promedio.
          <template v-if="horaPico">
            La hora con más pedidos fue <b>{{ hora(horaPico.hora) }}</b> ({{ horaPico.pedidos }}).
          </template>
          <template v-if="horaLenta && horaLenta !== horaPico">
            La más lenta fue <b>{{ hora(horaLenta.hora) }}</b> ({{ minutos(horaLenta.preparacion_promedio) }}).
          </template>
        </p>
        <div class="barras">
          <div v-for="h in horasConDatos" :key="h.hora" class="barra" :title="detalleHora(h)">
            <span class="barra__tiempo">{{ h.pedidos ? Math.round(h.preparacion_promedio) + "′" : "" }}</span>
            <div class="barra__fondo">
              <div class="barra__relleno" :class="{ 'barra__relleno--pico': h === horaPico }"
                :style="{ height: (h.pedidos / maximoPedidos) * 100 + '%' }"></div>
            </div>
            <span class="barra__hora">{{ h.hora }}</span>
            <span class="barra__pedidos">{{ h.pedidos || "" }}</span>
          </div>
        </div>

        <h3 class="seccion">Por día de la semana</h3>
        <v-simple-table dense>
          <thead>
            <tr><th>Día</th><th class="text-right">Pedidos</th><th class="text-right">Espera</th><th class="text-right">Preparación</th></tr>
          </thead>
          <tbody>
            <tr v-for="d in datos.por_dia" :key="d.dia">
              <td>{{ d.nombre }}</td>
              <td class="text-right">{{ d.pedidos }}</td>
              <td class="text-right">{{ minutos(d.espera_promedio) }}</td>
              <td class="text-right">{{ minutos(d.preparacion_promedio) }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <h3 class="seccion">Los 10 pedidos más lentos</h3>
        <v-simple-table dense>
          <thead>
            <tr><th>Mesa</th><th>Llegó</th><th class="text-right">Espera</th><th class="text-right">Preparación</th></tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in datos.mas_lentos" :key="i">
              <td>{{ p.mesa || "Sin mesa" }}</td>
              <td>{{ fecha(p.llegada) }}</td>
              <td class="text-right">{{ minutos(p.espera) }}</td>
              <td class="text-right font-weight-bold">{{ minutos(p.preparacion) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <p class="grey--text text--darken-1 mt-3 mb-0 pie">
          Un pedido que se devolvió desde «Listos» conserva su hora de inicio: su preparación incluye el tiempo que estuvo marcado como listo.
        </p>
      </template>
    </v-card>
  </div>
</template>

<script>
import * as moment from "moment-timezone";
import AppMenu from "../menu/AppMenu.vue";

export default {
  name: "TiemposDeCocina",
  components: { AppMenu },
  data: () => ({
    periodo: "7",
    desde: "",
    hasta: "",
    datos: null,
    cargando: false,
    breadcrumbs: [
      { text: "Inicio", disabled: false, to: "/pos", "exact-path": true },
      { text: "Reportes", disabled: false, "exact-path": true },
      { text: "Tiempos de cocina", disabled: true },
    ],
  }),
  computed: {
    sucursalNombre() {
      const s = this.$store.getters.store;
      return s ? s.name : "";
    },
    // De la primera a la última hora con pedidos, para no dibujar 24 barras casi vacías.
    horasConDatos() {
      if (!this.datos) return [];
      const con = this.datos.por_hora.filter((h) => h.pedidos > 0).map((h) => h.hora);
      if (!con.length) return [];
      return this.datos.por_hora.filter((h) => h.hora >= Math.min(...con) && h.hora <= Math.max(...con));
    },
    maximoPedidos() {
      return Math.max(1, ...this.horasConDatos.map((h) => h.pedidos));
    },
    horaPico() {
      return this.horasConDatos.reduce((m, h) => (h.pedidos > (m ? m.pedidos : 0) ? h : m), null);
    },
    horaLenta() {
      return this.horasConDatos.reduce((m, h) => (h.pedidos && h.preparacion_promedio > (m ? m.preparacion_promedio : -1) ? h : m), null);
    },
  },
  mounted() {
    this.elegirPeriodo();
  },
  methods: {
    elegirPeriodo() {
      const hoy = moment.tz("America/Bogota");
      if (this.periodo === "otro") {
        if (!this.desde) this.desde = hoy.clone().subtract(6, "days").format("YYYY-MM-DD");
        if (!this.hasta) this.hasta = hoy.format("YYYY-MM-DD");
        return;
      }
      this.hasta = hoy.format("YYYY-MM-DD");
      this.desde = hoy.clone().subtract(this.periodo === "hoy" ? 0 : Number(this.periodo) - 1, "days").format("YYYY-MM-DD");
      this.cargar();
    },
    async cargar() {
      const store = this.$store.getters.store;
      if (!store || !this.desde || !this.hasta) return;
      this.cargando = true;
      try {
        const res = await this.$http.get("api/v1/pos/reportes/tiempos-de-preparacion", {
          desde: this.desde,
          hasta: this.hasta,
          sucursal: store.bus_subsidiary_id.id,
        });
        this.datos = res && res.success ? res : null;
      } finally {
        this.cargando = false;
      }
    },
    // 75 → "1 h 15 min"; sin dato → "—".
    minutos(m) {
      if (m === null || m === undefined) return "—";
      const r = Math.round(m);
      return r < 60 ? `${r} min` : `${Math.floor(r / 60)} h ${r % 60} min`;
    },
    hora(h) {
      const f = (n) => `${n % 12 === 0 ? 12 : n % 12} ${n < 12 ? "am" : "pm"}`;
      return `${f(h)} - ${f((h + 1) % 24)}`;
    },
    detalleHora(h) {
      return `${this.hora(h.hora)}: ${h.pedidos} pedidos · espera ${this.minutos(h.espera_promedio)} · preparación ${this.minutos(h.preparacion_promedio)}`;
    },
    fecha(f) {
      return moment(f).format("DD/MM h:mm a");
    },
  },
};
</script>

<style scoped>
.filtros {
  gap: 12px;
}

.filtros__fecha {
  max-width: 170px;
}

.vacio {
  padding: 48px 16px;
  text-align: center;
  color: rgba(0, 0, 0, 0.5);
}

.dato {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8f2f4;
  border-left: 5px solid #a31d48;
}

.dato__titulo {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
}

.dato__valor {
  font-size: 1.9rem;
  font-weight: 800;
  line-height: 1.2;
  color: #4a1428;
}

.dato__nota {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);
}

.seccion {
  margin: 28px 0 8px;
  color: #4a1428;
}

.barras {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  overflow-x: auto;
  padding: 4px 0 8px;
}

.barra {
  display: flex;
  flex: 1 0 34px;
  max-width: 64px;
  flex-direction: column;
  align-items: center;
  min-width: 34px;
}

.barra__fondo {
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: 160px;
  border-radius: 6px 6px 0 0;
  background: #f3e9ed;
}

.barra__relleno {
  width: 100%;
  min-height: 2px;
  border-radius: 6px 6px 0 0;
  background: #c98aa0;
}

.barra__relleno--pico {
  background: #a31d48;
}

.barra__tiempo {
  height: 18px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #2e7d5b;
}

.barra__hora {
  margin-top: 4px;
  font-size: 0.85rem;
  font-weight: 700;
}

.barra__pedidos {
  height: 16px;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}

.pie {
  font-size: 0.85rem;
}
</style>
